import React from "react";
import { EmailContextAttributeResponse } from "../models/EmailModels";
import { Card, CardContent, Typography, Box, Divider, Accordion, AccordionSummary } from "@mui/material";
import { ExternalLink } from "./ExternalLink";
import EmailIcon from "@mui/icons-material/Email";
import { isValidUrl, isValidEmail } from "../utils/dataTypeValidation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ActionWithAiContextGroup: React.FC<{ title: string; values: EmailContextAttributeResponse[] }> = ({
  title,
  values,
}) => {
  // Find the first URL for the header
  const firstUrlIndex = values.findIndex((row) => isValidUrl(row.value));
  const firstUrlValue = firstUrlIndex !== -1 ? values[firstUrlIndex].value : null;

  // Filter out only the first URL from the main display, keep other URLs
  const contentValues = values.filter((row, index) => !(isValidUrl(row.value) && index === firstUrlIndex));

  const renderValue = (row: EmailContextAttributeResponse) => {
    const { name, value } = row;

    if (isValidUrl(value)) {
      return (
        <ExternalLink href={value} sx={{ textDecoration: "none", color: "primary.main" }}>
          {value}
        </ExternalLink>
      );
    }

    if (isValidEmail(value)) {
      return (
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            textDecoration: "none",
            wordBreak: "break-all",
          }}
        >
          <EmailIcon sx={{ fontSize: 14 }} />
          {value}
        </Typography>
      );
    }

    return (
      <Typography
        variant="body2"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "normal",
          fontSize: "0.875rem",
          color: "text.primary",
        }}
      >
        {value}
      </Typography>
    );
  };

  return (
    <Accordion
      defaultExpanded={true}
      sx={{
        border: 1,
        borderColor: "rgba(0, 0, 0, 0.23)",
        borderRadius: 1,
        padding: "0 8px",
        "&.Mui-expanded": {
          paddingBottom: "8px !important",
        },
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${title}-content`}
        id={`${title}-header`}
        sx={{
          border: "none",
          background: "transparent",
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "14px", flexGrow: 1 }}>
          {title}
        </Typography>
        {firstUrlValue && (
          <Typography variant="body2" sx={{ textDecoration: "none", color: "primary.main", fontSize: "12px" }}>
            <ExternalLink href={firstUrlValue} sx={{ textDecoration: "none", color: "primary.main", fontSize: "12px" }}>
              View
            </ExternalLink>
          </Typography>
        )}
      </AccordionSummary>

      {contentValues.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {contentValues.map((row, index) => (
            <Box key={index}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  marginBottom: 0.5,
                }}
              >
                {row.name}
              </Typography>
              <Box sx={{ paddingLeft: 0.5 }}>{renderValue(row)}</Box>
              {index < contentValues.length - 1 && <Divider sx={{ marginTop: 1.5, opacity: 0.3 }} />}
            </Box>
          ))}
        </Box>
      )}
    </Accordion>
  );
};

export default ActionWithAiContextGroup;
